import type { WizardStatus } from '@risksmart-app/domain/src/types/consts/wizard-status';
import type { UpdateWizardMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type UpdateWizardTRPCInput = {
  RiskId: string;
  CurrentStep: number;
  Status?: WizardStatus | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  affectedRows: number;
}): UpdateWizardMutation => {
  return {
    updateWizardById: {
      __typename: 'GenericMutationOutput',
      affected_rows: trpcData.affectedRows,
    },
  };
};

export const useUpdateWizardTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.wizard.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.wizard.wizards.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.wizard.wizardById.queryKey(),
        });
      },
    }),
  });

  return {
    updateWizard: async (
      input: UpdateWizardTRPCInput
    ): Promise<UpdateWizardMutation> => {
      const result = await mutation.mutateAsync(input);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
